/**
 * ============================================================
 * tile.co.uk — Cart Adhesive Recommendation Engine
 * ============================================================
 * Reads tile products in the cart via the Shopify Cart API,
 * calculates total m² per tile line item, maps material and
 * tile size to the correct adhesive recommendation, and renders
 * a suggestion panel inside the cart drawer.
 *
 * DEPENDENCIES:
 * - tile-metafield-cache.liquid must be rendered on product
 *   pages to populate the metafield cache.
 * - The cart drawer must have an element with
 *   id="adhesive-recommendation-target" where the panel
 *   will be injected.
 *
 * DEVELOPER NOTES:
 * - This script is self-contained and uses no external libraries.
 * - Call TileAdhesiveRecommender.init() once the cart drawer
 *   opens or cart contents change.
 *
 * SIZE LOGIC:
 * - Ready-mixed adhesives are limited to tiles with BOTH
 *   dimensions ≤ 300mm. If EITHER dimension exceeds 300mm,
 *   a cement-based adhesive is recommended regardless of material.
 * - Porcelain always gets cement-based regardless of size.
 * - 20mm porcelain tiles are outdoor paving — excluded entirely.
 *
 * PRODUCT HANDLE NOTES (verified July 2026):
 * - H40 Gel grey + white are combined as variants on one product.
 *   The JS links to the product page (not variant-specific) —
 *   the customer selects their colour on the product page.
 * - Setaflex Standard Set grey is ARCHIVED (zero stock).
 *   Recommendations default to white.
 * - Setaflex Rapid Set is white only.
 * ============================================================
 */

var TileAdhesiveRecommender = (function () {

  // ============================================================
  // CONFIG
  // ============================================================
  var CONFIG = {
    shopDomain: window.Shopify && window.Shopify.shop ? window.Shopify.shop : 'tile-co-uk.myshopify.com',
    targetElementId: 'adhesive-recommendation-target',
    minimumM2: 0.1,

    // Maximum tile dimension (mm) for which ready-mixed
    // adhesives are suitable. If EITHER dimension exceeds
    // this value, cement-based adhesive is required.
    readyMixedMaxDimensionMm: 300,
  };


  // ============================================================
  // MATERIAL + WALL/FLOOR + SIZE → ADHESIVE TYPE LOOKUP
  //
  // SIZE RULES (applied before material lookup):
  // - If either tile dimension > 300mm → force cement_large_format
  //   for ceramic; porcelain always cement regardless of size.
  // - Ready-mixed only available for ceramic wall ≤ 300×300mm.
  //
  // MATERIAL KEYS map to adhesive types:
  // - ceramic: wall vs floor split, further split by size
  // - porcelain: always cement-based (no size exception)
  // - natural stone types: cement-based H40 Gel
  // ============================================================
  var MATERIAL_MAP = {
    'ceramic':                   { wall: 'ceramic_wall', floor: 'ceramic_floor' },
    'fully vitrified ceramic':   { wall: 'ceramic_wall', floor: 'ceramic_floor' },
    'porcelain':                 'porcelain',
    'glazed porcelain':          'porcelain',
    // glass removed — no suitable stocked adhesive currently
    'limestone':                 'moisture_sensitive_stone',
    'marble':                    'moisture_sensitive_stone',
    'travertine':                'moisture_sensitive_stone',
    'sandstone':                 'moisture_sensitive_stone',
    'slate':                     'natural_stone',
    'quartzite':                 'natural_stone',
    'terracotta':                'natural_stone',
  };


  // ============================================================
  // ADHESIVE TYPE → PRODUCT RECOMMENDATION
  //
  // Verified handles (July 2026):
  // - bal-15kg-green-star-wall-tile-adhesive
  // - kerakoll-h40-gel-c2-te-s1-standard-set-flexible-tile-adhesive-20kg
  //   (grey + white variants on same product)
  // - kerakoll-setaflex-standard-set-s1-white-tile-adhesive-20kg
  //   (white only — grey archived)
  // - kerakoll-setaflex-rapid-set-s1-white-tile-adhesive-20kg
  //   (white only)
  // ============================================================
  var ADHESIVE_RECOMMENDATIONS = {

    // Ceramic wall tile ≤ 300mm in both dimensions
    ceramic_wall: {
      label:                'Ceramic wall tiles (up to 300×300mm)',
      product_handle:       'bal-15kg-green-star-wall-tile-adhesive',
      product_name:         'BAL Green Star',
      colour:               'white',
      colour_note:          null,
      coverage_m2_per_unit: 6.0,   // 0.4 m²/kg dry wall × 15kg — BAL datasheet
      bags_kg:              15,
      bag_label:            'tub',
      note:                 'Ready-mixed — no mixing required. Suitable for ceramic wall tiles up to 300×300mm including bathrooms, kitchens and shower areas.',
    },

    // Ceramic wall tile where EITHER dimension > 300mm
    ceramic_wall_large: {
      label:                'Large format ceramic wall tiles (over 300mm)',
      product_handle:       'kerakoll-setaflex-standard-set-s1-white-tile-adhesive-20kg',
      product_name:         'Kerakoll Setaflex Standard Set S1',
      colour:               'white',
      colour_note:          null,
      coverage_kg_per_m2:   3.6,   // 1.2 kg/m²/mm × 3mm bed — Kerakoll datasheet (white)
      bags_kg:              20,
      bag_label:            'bag',
      note:                 'Cement-based powder adhesive — requires mixing with water on site. S1 flexible — suitable for large format ceramic wall tiles.',
    },

    // Ceramic floor tile ≤ 300mm in both dimensions
    ceramic_floor: {
      label:                'Ceramic floor tiles (up to 300×300mm)',
      product_handle:       'kerakoll-setaflex-standard-set-s1-white-tile-adhesive-20kg',
      product_name:         'Kerakoll Setaflex Standard Set S1',
      colour:               'white',
      colour_note:          null,
      coverage_kg_per_m2:   3.6,   // 1.2 kg/m²/mm × 3mm bed — Kerakoll datasheet (white)
      bags_kg:              20,
      bag_label:            'bag',
      note:                 'Cement-based powder adhesive — requires mixing with water on site. S1 flexible — suitable for floors including underfloor heating.',
    },

    // Ceramic floor tile where EITHER dimension > 300mm
    ceramic_floor_large: {
      label:                'Large format ceramic floor tiles (over 300mm)',
      product_handle:       'kerakoll-setaflex-standard-set-s1-white-tile-adhesive-20kg',
      product_name:         'Kerakoll Setaflex Standard Set S1',
      colour:               'white',
      colour_note:          null,
      coverage_kg_per_m2:   3.6,   // 1.2 kg/m²/mm × 3mm bed — Kerakoll datasheet (white)
      bags_kg:              20,
      bag_label:            'bag',
      note:                 'Cement-based powder adhesive — requires mixing with water on site. S1 flexible — suitable for large format floor tiles and underfloor heating.',
    },

    // Porcelain — always cement-based regardless of size
    porcelain: {
      label:                'Porcelain tiles',
      product_handle:       'kerakoll-setaflex-rapid-set-s1-white-tile-adhesive-20kg',
      product_name:         'Kerakoll Setaflex Rapid Set S1',
      colour:               'white',
      colour_note:          null,
      coverage_kg_per_m2:   3.6,   // 1.2 kg/m²/mm × 3mm bed — Kerakoll datasheet (white)
      bags_kg:              20,
      bag_label:            'bag',
      note:                 'Cement-based powder adhesive — requires mixing with water on site.',
    },

    // Moisture-sensitive natural stone — white adhesive essential
    moisture_sensitive_stone: {
      label:                'Moisture-sensitive natural stone',
      product_handle:       'kerakoll-h40-gel-c2-te-s1-standard-set-flexible-tile-adhesive-20kg',
      product_name:         'Kerakoll H40 Gel',
      colour:               'white',
      colour_note:          'White adhesive is essential for limestone, marble, travertine and sandstone to prevent water staining through the tile.',
      coverage_kg_per_m2:   3.6,   // 1.2 kg/m²/mm × 3mm bed — Kerakoll datasheet (white)
      bags_kg:              20,
      bag_label:            'bag',
      note:                 'Moisture-sensitive stone such as limestone, marble, travertine and sandstone requires a specialist white adhesive to prevent staining.',
    },

    // Non-moisture-sensitive natural stone
    natural_stone: {
      label:                'Natural stone tiles',
      product_handle:       'kerakoll-h40-gel-c2-te-s1-standard-set-flexible-tile-adhesive-20kg',
      product_name:         'Kerakoll H40 Gel',
      colour:               'grey or white',
      colour_note:          null,
      coverage_kg_per_m2:   3.9,   // 1.3 kg/m²/mm × 3mm bed — Kerakoll datasheet (grey)
      bags_kg:              20,
      bag_label:            'bag',
      note:                 'Cement-based powder adhesive — requires mixing with water on site.',
    },

  };


  // ============================================================
  // WALL/FLOOR DETECTION
  // Returns true if this product is wall-only.
  // Anything containing "Wall" but NOT "Floor" = wall only.
  // ============================================================
  function isWallOnly(wallOrFloor) {
    if (!wallOrFloor) return false;
    var v = wallOrFloor.toLowerCase();
    return v.indexOf('wall') !== -1 && v.indexOf('floor') !== -1
      ? false
      : v.indexOf('wall') !== -1;
  }


  // ============================================================
  // PARSE TILE DIMENSIONS
  // Attempts to parse width and height in mm from the
  // tile size metafield (e.g. "600x300mm", "300 x 600 mm",
  // "75x300", "300×600mm"). Returns { w, h } or null.
  // Accepts both × and x as separator.
  // ============================================================
  function parseDimensions(sizeValue) {
    if (!sizeValue) return null;

    // Normalise: lower, strip spaces around separators
    var s = sizeValue.toString().toLowerCase().replace(/\s/g, '');

    // Match patterns like: 300x600mm, 300×600mm, 300x600, 600x300
    var match = s.match(/^(\d+(?:\.\d+)?)[x×](\d+(?:\.\d+)?)/);
    if (!match) return null;

    var a = parseFloat(match[1]);
    var b = parseFloat(match[2]);
    if (isNaN(a) || isNaN(b) || a <= 0 || b <= 0) return null;

    return { w: a, h: b };
  }


  // ============================================================
  // IS LARGE FORMAT
  // Returns true if EITHER dimension exceeds the ready-mixed
  // maximum (300mm). Used to escalate ceramic tiles from
  // ready-mixed to cement-based.
  // ============================================================
  function isLargeFormat(sizeValue) {
    var dims = parseDimensions(sizeValue);
    if (!dims) return false;  // unknown size — assume small, don't block
    var max = CONFIG.readyMixedMaxDimensionMm;
    return dims.w > max || dims.h > max;
  }


  // ============================================================
  // SOLD-BY NORMALISATION
  // ============================================================
  function normaliseSoldBy(value) {
    if (!value) return null;
    var v = value.toLowerCase().trim();
    if (v === 'm2' || v === 'm²')          return 'm2';
    if (v === 'box' || v === 'pack')       return 'box';
    if (v === 'sheet' || v === 'mosaic')   return 'sheet';
    if (
      v === 'tile' || v === 'piece' ||
      v.indexOf('tile set') !== -1 ||
      v.indexOf('-tile set') !== -1
    )                                      return 'tile';
    return v;
  }


  // ============================================================
  // IS TILE PRODUCT
  // ============================================================
  function isTileProduct(soldBy) {
    var norm = normaliseSoldBy(soldBy);
    return norm === 'box' || norm === 'sheet' || norm === 'tile' || norm === 'm2';
  }


  // ============================================================
  // CALCULATE M²
  // ============================================================
  function calculateM2(quantity, calculatorValue, soldBy) {
    var norm = normaliseSoldBy(soldBy);
    if (!calculatorValue || isNaN(calculatorValue) || calculatorValue <= 0) {
      return 0;
    }
    if (norm === 'm2') {
      return quantity;
    }
    var m2PerUnit = 1 / calculatorValue;
    return quantity * m2PerUnit;
  }


  // ============================================================
  // CALCULATE BAGS NEEDED
  // Supports two calculation methods:
  // 1. coverage_m2_per_unit — ready-mixed products (m² per tub)
  // 2. coverage_kg_per_m2  — cement powder products (kg per m²)
  // Adds 10% buffer to account for waste and cuts.
  // ============================================================
  function calculateBags(totalM2, adhesive) {
    if (totalM2 <= 0) return 0;
    if (adhesive.coverage_m2_per_unit) {
      return Math.ceil((totalM2 * 1.1) / adhesive.coverage_m2_per_unit);
    }
    var totalKgNeeded = totalM2 * adhesive.coverage_kg_per_m2 * 1.1;
    return Math.ceil(totalKgNeeded / adhesive.bags_kg);
  }


  // ============================================================
  // GET ADHESIVE TYPE
  // Applies rules in order:
  // 1. Exclude 20mm porcelain (outdoor paving)
  // 2. Material lookup
  // 3. For ceramic: apply size check and wall/floor split
  // 4. Porcelain: always cement regardless of size
  // ============================================================
  function getAdhesiveType(material, wallOrFloor, tags, thickness, tileSize) {
    if (!material) return null;

    // Rule 1: Exclude 20mm porcelain paving tiles
    if (thickness) {
      var t = thickness.toString().replace(/\s/g, '').toLowerCase();
      var isPorcelain = (
        material.toLowerCase() === 'porcelain' ||
        material.toLowerCase() === 'glazed porcelain'
      );
      if ((t === '20mm' || t === '20') && isPorcelain) return null;
    }

    var key = material.toLowerCase().trim();
    var mapping = MATERIAL_MAP[key];
    if (!mapping) return null;

    // Rule 2: Non-ceramic materials — no size check needed
    if (typeof mapping === 'string') {
      return mapping;
    }

    // Rule 3: Ceramic — apply size check then wall/floor split
    if (typeof mapping === 'object' && mapping.wall && mapping.floor) {
      var large = isLargeFormat(tileSize);
      var wall   = isWallOnly(wallOrFloor);

      if (large) {
        return wall ? 'ceramic_wall_large' : 'ceramic_floor_large';
      }
      return wall ? mapping.wall : mapping.floor;
    }

    return null;
  }


  // ============================================================
  // FETCH PRODUCT METAFIELDS FROM CACHE
  // Reads from TileMetafieldCache (populated by
  // tile-metafield-cache.liquid on product pages) and
  // sessionStorage for products viewed earlier in the session.
  // ============================================================
  function fetchProductMetafields(productIds, callback) {
    var cache = {};

    try {
      var stored = JSON.parse(sessionStorage.getItem('TileMetafieldCache') || '{}');
      Object.keys(stored).forEach(function (id) { cache[id] = stored[id]; });
    } catch (e) {}

    if (window.TileMetafieldCache) {
      Object.keys(window.TileMetafieldCache).forEach(function (id) {
        cache[id] = window.TileMetafieldCache[id];
      });
    }

    var result  = {};
    var missing = [];

    productIds.forEach(function (pid) {
      if (cache[pid]) {
        result[pid] = cache[pid];
      } else {
        missing.push(pid);
      }
    });

    if (missing.length > 0) {
      console.info(
        '[TileAdhesiveRecommender] ' + missing.length + ' product(s) not in metafield cache. ' +
        'IDs: ' + missing.join(', ')
      );
    }

    callback(null, result);
  }


  // ============================================================
  // ANALYSE CART
  // ============================================================
  function analyseCart(callback) {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var productIds = [];

        cart.items.forEach(function (item) {
          var pid = String(item.product_id);
          if (productIds.indexOf(pid) === -1) {
            productIds.push(pid);
          }
        });

        if (productIds.length === 0) {
          return callback(null, []);
        }

        fetchProductMetafields(productIds, function (err, metafields) {
          if (err) return callback(err, null);

          var tileItems = [];

          cart.items.forEach(function (item) {
            var pid  = String(item.product_id);
            var meta = metafields[pid];
            if (!meta) return;

            var soldBy = meta.sold_by;
            if (!isTileProduct(soldBy)) return;

            var calculatorValue = meta.calculator;
            var m2              = calculateM2(item.quantity, calculatorValue, soldBy);

            // Read tile size from correct metafield key.
            // Primary:  custom_attributes.dimensions (e.g. "300x750mm")
            // Fallback: tags.size (e.g. "30x75cm") if dimensions not set
            var tileSize = meta.dimensions || meta['tags.size'] || null;
            var adhesiveType = getAdhesiveType(
              meta.material,
              meta.wall_or_floor,
              meta.tags,
              meta.thickness,
              tileSize
            );

            if (m2 > 0 && adhesiveType) {
              tileItems.push({
                title:         item.title,
                quantity:      item.quantity,
                sold_by:       soldBy,
                material:      meta.material,
                wall_or_floor: meta.wall_or_floor,
                tile_size:     tileSize,
                m2:            m2,
                adhesive_type: adhesiveType,
              });
            }
          });

          var cartHandles = cart.items.map(function (item) {
            return item.handle || (item.url
              ? item.url.replace('/products/', '').split('?')[0]
              : '');
          });

          callback(null, tileItems, cartHandles);
        });
      })
      .catch(function (err) {
        callback(err, null);
      });
  }


  // ============================================================
  // PRIORITY ORDER
  // When multiple tile types are in the cart, always recommend
  // the highest-requirement adhesive.
  // cement_large_format types rank above their standard equivalents
  // so a mixed cart with one large tile gets cement recommended.
  // ============================================================
  var PRIORITY = {
    moisture_sensitive_stone: 8,
    natural_stone:            7,
    porcelain:                6,
    ceramic_floor_large:      5,
    ceramic_wall_large:       4,
    ceramic_floor:            3,
    ceramic_wall:             2,
  };


  // ============================================================
  // BUILD RECOMMENDATION
  // ============================================================
  function buildRecommendation(tileItems, cartHandles) {
    if (!tileItems || tileItems.length === 0) return null;
    cartHandles = cartHandles || [];

    // Find highest priority adhesive type needed
    var highestPriority = 0;
    var recommendedType = null;

    tileItems.forEach(function (item) {
      var p = PRIORITY[item.adhesive_type] || 0;
      if (p > highestPriority) {
        highestPriority = p;
        recommendedType = item.adhesive_type;
      }
    });

    if (!recommendedType) return null;

    var adhesive = ADHESIVE_RECOMMENDATIONS[recommendedType];
    if (!adhesive) return null;

    // If recommended adhesive already in cart, hide panel
    var alreadyInCart = cartHandles.some(function (handle) {
      return handle === adhesive.product_handle;
    });
    if (alreadyInCart) return null;

    // Sum m² only for tiles that need THIS adhesive type
    var totalM2 = 0;
    tileItems.forEach(function (item) {
      if (item.adhesive_type === recommendedType) {
        totalM2 += item.m2;
      }
    });

    if (totalM2 < CONFIG.minimumM2) return null;

    var bagsNeeded = calculateBags(totalM2, adhesive);

    return {
      total_m2:      Math.round(totalM2 * 100) / 100,
      adhesive_type: recommendedType,
      adhesive:      adhesive,
      bags_needed:   bagsNeeded,
    };
  }


  // ============================================================
  // ADD TO CART
  // Adds the recommended adhesive and triggers Noor's native
  // cart drawer re-render.
  // ============================================================
  function addToCart(variantId, quantity, buttonEl) {
    buttonEl.disabled    = true;
    buttonEl.textContent = 'Adding…';

    var cartDrawerSection = document.querySelector('[cart-drawer-section]');
    var sectionId         = cartDrawerSection ? cartDrawerSection.dataset.id : null;

    var body = { id: variantId, quantity: quantity };

    if (sectionId) {
      body.sections     = [sectionId];
      body.sections_url = window.location.pathname;
    }

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify(body),
    })
    .then(function (res) { return res.json(); })
    .then(function (response) {
      if (response.status) {
        throw new Error(response.description || 'Could not add to cart');
      }

      var cartDrawerEl = document.querySelector('cart-drawer');
      if (
        cartDrawerEl &&
        typeof cartDrawerEl.renderContents === 'function' &&
        response.sections
      ) {
        cartDrawerEl.renderContents(response);
        setTimeout(function () { init(); }, 1400);
      } else {
        buttonEl.textContent  = '✓ Added to cart';
        buttonEl.style.background = '#2d6a1f';
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var countEl = document.querySelector('[data-cart-count]');
            if (countEl) countEl.textContent = cart.item_count;
          });
      }
    })
    .catch(function () {
      buttonEl.disabled         = false;
      buttonEl.textContent      = 'Error — try again';
      buttonEl.style.background = '#c0392b';
    });
  }


  // ============================================================
  // FETCH PRODUCT DATA (variant ID, thumbnail, price)
  // ============================================================
  function fetchProductData(handle, callback) {
    fetch('/products/' + handle + '.js')
      .then(function (res) { return res.json(); })
      .then(function (product) {
        var variant = product.variants && product.variants[0]
          ? product.variants[0]
          : null;
        callback(null, {
          variantId:     variant ? variant.id   : null,
          price:         variant ? variant.price : null,
          featuredImage: product.featured_image  || null,
          title:         product.title           || '',
        });
      })
      .catch(function (err) { callback(err, null); });
  }


  // ============================================================
  // RENDER PANEL
  // ============================================================
  function renderPanel(recommendation) {
    var target = document.getElementById(CONFIG.targetElementId);
    if (!target) return;

    if (!recommendation) {
      target.innerHTML = '';
      return;
    }

    var a              = recommendation.adhesive;
    var m2Display      = recommendation.total_m2.toFixed(2);
    var bagsDisplay    = recommendation.bags_needed;
    var bagLabel       = a.bag_label || 'bag';
    var bagLabelPlural = bagsDisplay !== 1 ? bagLabel + 's' : bagLabel;

    var colourNote = a.colour_note
      ? '<p style="margin:6px 0 0;font-size:12px;color:#c47d00;">' + a.colour_note + '</p>'
      : '';

    var btnId   = 'adhesive-add-btn-'   + Date.now();
    var priceId = 'adhesive-price-'     + btnId;

    var html = [
      '<div style="',
        'margin:16px 0 0;',
        'padding:14px 16px;',
        'background:#faf4f0;',
        'border:1px solid #e0d4c8;',
        'border-radius:8px;',
        'font-family:inherit;',
        'font-size:13px;',
        'color:#1a1a1a;',
        'line-height:1.5;',
      '">',

        // Header
        '<div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">',
          '<svg width="18" height="18" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">',
            '<rect x="3" y="2" width="5" height="13" rx="1.5" fill="#3c0000"/>',
            '<rect x="1" y="15" width="9" height="10" rx="1" fill="#3c0000"/>',
            '<rect x="1" y="25" width="2" height="3" rx="0.5" fill="#3c0000"/>',
            '<rect x="4.5" y="25" width="2" height="3" rx="0.5" fill="#3c0000"/>',
            '<rect x="8" y="25" width="2" height="3" rx="0.5" fill="#3c0000"/>',
            '<rect x="13" y="15" width="22" height="2" rx="1" fill="#3c0000" opacity="0.4"/>',
            '<rect x="13" y="19" width="22" height="2" rx="1" fill="#3c0000" opacity="0.4"/>',
            '<rect x="13" y="23" width="22" height="2" rx="1" fill="#3c0000" opacity="0.4"/>',
          '</svg>',
          '<span style="font-weight:600;font-size:13px;color:#3c0000;">You\'ll also need adhesive</span>',
        '</div>',

        // m² summary
        '<p style="margin:0 0 8px;font-size:12px;color:#888;">',
          'Based on <strong style="color:#1a1a1a;">' + m2Display + ' m²</strong> of tiles in your cart.',
        '</p>',

        // Product card
        '<div style="',
          'background:#fff;',
          'border:1px solid #e5e5e3;',
          'border-radius:6px;',
          'padding:10px 12px;',
          'margin-bottom:10px;',
          'display:flex;',
          'gap:12px;',
          'align-items:flex-start;',
        '">',
          '<div id="adhesive-thumb-' + btnId + '" style="',
            'width:56px;height:56px;flex-shrink:0;',
            'border-radius:4px;background:#f0ebe5;overflow:hidden;',
          '"></div>',
          '<div style="flex:1;min-width:0;">',
            '<div style="font-weight:600;font-size:13px;margin-bottom:2px;">' + a.product_name + '</div>',
            '<div style="font-size:12px;color:#888;margin-bottom:4px;">' + a.label + ' · ' + a.colour + '</div>',
            '<div style="font-size:13px;color:#1a1a1a;">',
              '<strong>' + bagsDisplay + ' ' + bagLabelPlural + '</strong> recommended',
              ' <span style="font-size:11px;color:#aaa;">(inc. 10% for waste)</span>',
            '</div>',
            '<div id="' + priceId + '" style="font-size:13px;color:#1a1a1a;margin-top:4px;"></div>',
            colourNote,
          '</div>',
        '</div>',

        // Add to cart button
        '<button id="' + btnId + '" style="',
          'display:block;width:100%;text-align:center;',
          'background:#3c0000;color:#fff;',
          'font-size:13px;font-weight:600;',
          'padding:10px 16px;border-radius:6px;border:none;',
          'cursor:pointer;margin-top:10px;font-family:inherit;',
        '">',
          'Add ' + bagsDisplay + ' ' + bagLabelPlural + ' of ' + a.product_name + ' to cart',
        '</button>',

        '<p style="text-align:center;margin:8px 0 0;">',
          '<a href="/products/' + a.product_handle + '" style="font-size:12px;color:#3c0000;">',
            'View product details',
          '</a>',
        '</p>',

      '</div>',
    ].join('');

    target.innerHTML = html;

    // Fetch variant ID, thumbnail and price in one call
    fetchProductData(a.product_handle, function (err, data) {
      if (!err && data) {

        // Thumbnail
        var thumbEl = document.getElementById('adhesive-thumb-' + btnId);
        if (thumbEl && data.featuredImage) {
          var imgUrl = data.featuredImage.replace(
            /(\.[^.]+)$/, '_80x80_crop_center$1'
          );
          thumbEl.innerHTML =
            '<img src="' + imgUrl + '" alt="' + data.title + '" ' +
            'style="width:100%;height:100%;object-fit:cover;">';
        }

        // Price
        var priceEl = document.getElementById(priceId);
        if (priceEl && data.price) {
          var unitPrice        = data.price / 100;
          var totalPrice       = (unitPrice * bagsDisplay).toFixed(2);
          var unitPriceDisplay = unitPrice.toFixed(2);
          priceEl.innerHTML =
            '<strong style="color:#3c0000;">£' + totalPrice + '</strong>' +
            ' <span style="font-size:11px;color:#aaa;">(£' + unitPriceDisplay + ' each)</span>';
        }

        // Add to cart button
        if (data.variantId) {
          var btn = document.getElementById(btnId);
          if (btn) {
            btn.addEventListener('click', function () {
              addToCart(data.variantId, bagsDisplay, btn);
            });
          }
        }
      }
    });
  }


  // ============================================================
  // PUBLIC INIT
  // ============================================================
  function init() {
    analyseCart(function (err, tileItems, cartHandles) {
      if (err) {
        console.warn('[TileAdhesiveRecommender] Error fetching cart:', err);
        return;
      }
      var recommendation = buildRecommendation(tileItems, cartHandles);
      renderPanel(recommendation);
    });
  }

  return { init: init };

})();